import React, { useEffect, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import { ReactComponent as Funnel } from 'src/Images/icons/common/funnel.svg';
import { ReactComponent as BulletPoints } from 'src/Images/icons/common/bulletpoints.svg';
import { ReactComponent as Refresh } from 'src/Images/icons/common/refresh.svg';
import { ReactComponent as Search } from 'src/Images/icons/common/search.svg';
import { ReactComponent as ArrowLeft } from 'src/Images/icons/common/arrowleft.svg';
import { ReactComponent as ArrowRight } from 'src/Images/icons/common/arrowright.svg';
import { ReactComponent as Calendar } from 'src/Images/icons/common/calendar.svg';
import Table from 'src/components/PendingGrnVehicleEntry/List/Table';
import getColorCode from 'src/utils/GetThemeColor';
import { useDispatch, useSelector } from 'react-redux';
import 'src/style/tableList.css';
import usePageTitle from 'src/utils/SetPageTitle';
import TableRightDrawer from 'src/components/Product/List/TableRightDrawer';
import TableContent from 'src/components/Product/List/TableContent';
import { getProductList } from 'src/redux/Actions/Product';
import { Row, Col } from 'react-bootstrap';
import FilterMultiSelect from 'src/utils/FilterMultiSelect';
import { CustomToolTip } from 'src/utils/CustomToolTip';
import Breadcrumb from 'src/components/Breadcrumb';
import { useSetBreadcrumbData } from 'src/utils/SetBreadcrumbData';
import { useLocation } from 'react-router-dom';

export default function ProductList() {
  usePageTitle('Cloud VT • Products');
  const location = useLocation();
  const dispatch = useDispatch();

  const [draggedFieldOrder, setDraggedFieldOrder] = useState([]);
  const [showFunnel, setShowFunnel] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchInput, setSearchInput] = useState(null);
  const productReducer = useSelector((state) => state.productReducer);
  const { totalPages } = productReducer.products;
  const fieldFilter = productReducer?.products?.fieldMapping;
  const authReducer = useSelector((state) => state.AuthReducer);
  const themeColor = authReducer?.userData?.tenant?.theme;
  const tenantOrganisationId = authReducer.userData.organisations?.id;
  const dayFilterMapping = {
    1: 'Recent Product',
    2: 'Last 7 Days',
    3: 'All 15 Days',
    4: 'Month Ago',
  };
  const [selectedFilterOption, setSelectedFilterOption] = useState(null);
  const [tableSortOrder, setTableSortOrder] = useState(null);
  const [tableSortField, setTableSortField] = useState(null);
  const [selectedFieldFilter, setSelectedFieldFilter] = useState([
    '1',
    '2',
    '3',
    '4',
    '5',
  ]);
  const tableHeaders = productReducer.products?.selectedFields;
  const [searchInputChange, setSearchInputChange] = useState(null);
  const searchTexts = ['Name', 'Code'];
  const tableFieldFilter =
    draggedFieldOrder.length > 0 ? draggedFieldOrder : selectedFieldFilter;

  useEffect(() => {
    const interval = setInterval(() => {
      setSearchInputChange(
        (prev) =>
          searchTexts[(searchTexts.indexOf(prev) + 1) % searchTexts.length],
      );
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleSelectedFieldFilter = (e, value) => {
    const updatedSelectedFieldFilter = [...selectedFieldFilter];
    if (e.target.checked) {
      updatedSelectedFieldFilter.push(value);
    } else {
      const index = updatedSelectedFieldFilter.indexOf(value);
      if (index > -1) {
        updatedSelectedFieldFilter.splice(index, 1);
      }
    }
    setSelectedFieldFilter(updatedSelectedFieldFilter);
  };

  const handleTableSortOrder = (value, field) => {
    setTableSortOrder(value);
    setTableSortField(field);
  };

  const handleSelectChange = (e) => {
    setSelectedFilterOption(e.target.value);
  };

  const handleFunnelToggle = () => {
    setShowFunnel(!showFunnel);
  };

  const [openDrawer, setOpenDrawer] = useState(false);

  const toggleDrawer = (open) => () => {
    setOpenDrawer(open);
  };

  useEffect(() => {
    dispatch(
      getProductList(
        tenantOrganisationId,
        pageNumber,
        searchInput,
        selectedFilterOption,
        tableSortOrder,
        tableSortField,
        tableFieldFilter,
      ),
    );
  }, [
    dispatch,
    pageNumber,
    searchInput,
    tableSortOrder,
    tableSortField,
    selectedFieldFilter,
    tableFieldFilter,
    selectedFilterOption,
  ]);

  const handleSearchInput = (e) => {
    const { value } = e.target;
    setSearchInput(value);
  };

  const handleInputChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!Number.isNaN(value) && value >= 1 && value <= totalPages) {
      setPageNumber(value);
    }
  };

  const handleNextPage = () => {
    if (pageNumber < totalPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const [formData, setFormData] = useState({
    selectedProductType: [],
    createDate: null,
  });

  const handleSearchInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const productTypeDataOptions = [
    { value: 1, label: 'Raw Material' },
    { value: 2, label: 'Semi-Finished' },
    { value: 3, label: 'Finished' },
  ];

  function extractIds(selectedItems) {
    return selectedItems.map((item) => item.value);
  }

  function handleSearchFilter() {
    const searchPayload = {
      product_type_key:
        formData.selectedProductType.length > 0
          ? extractIds(formData.selectedProductType)
          : null,
      created: formData.createDate || null,
    };

    const params = new URLSearchParams();
    Object.entries(searchPayload).forEach(([key, value]) => {
      Array.isArray(value)
        ? value.forEach((item) => params.append(key, item))
        : value && params.append(key, value);
    });

    const queryString = params.toString();

    dispatch(
      getProductList(
        tenantOrganisationId,
        pageNumber,
        searchInput,
        selectedFilterOption,
        tableSortOrder,
        tableSortField,
        tableFieldFilter,
        queryString,
      ),
    );
  }

  function handleResetFilter() {
    setSelectedFieldFilter(['1', '2', '3', '4']);
    setFormData({
      selectedProductType: [],
      createDate: null,
    });
  }

  useSetBreadcrumbData(location.pathname, 'Products');

  return (
    <div className="div-flex-column page-layout">
      <div className="div-flex-column">
        <div className="div-space-between left-padding">
          <div className="div-flex-row cg-5">
            <Breadcrumb />
          </div>
          <Col xxl={2} xl={2} lg={2} className="p-0 m-0">
            <div className="input-wrapper">
              <input
                type="text"
                className="form-input-section input-icon-remove"
                placeholder={`Search by ${searchInputChange}`}
                value={searchInput}
                onChange={handleSearchInput}
              />
              <span className="form-input-section-icon">
                <Search
                  className="icon-size-lg"
                  color={getColorCode(themeColor)}
                />
              </span>
            </div>
          </Col>
        </div>
        <div className="div-space-between border-bottom slider-padding">
          <span className="text-xxl text-bold left-padding">Products</span>
          <div className="div-flex-row div-align-center cg-20 right-padding">
            <div className="div-flex-row cg-20 div-align-center">
              <div className="list-container div-flex-column rg-5">
                <CustomToolTip title="Filter" placement="bottom">
                  <button
                    aria-label="Funnel"
                    className="icon-btn-none"
                    onClick={handleFunnelToggle}
                  >
                    <Funnel
                      className="icon-size-lg"
                      color={showFunnel ? getColorCode(themeColor) : '#9F9F9F'}
                    />
                  </button>
                </CustomToolTip>
              </div>
              <div className="button-container">
                <CustomToolTip title="Organiser" placement="bottom">
                  <button
                    aria-label="Funnel"
                    className="icon-btn-none"
                    onClick={toggleDrawer(true)}
                  >
                    <BulletPoints className="icon-size-lg" color="#9F9F9F" />
                  </button>
                </CustomToolTip>
                <Drawer
                  anchor="right"
                  open={Boolean(openDrawer)}
                  onClose={toggleDrawer(false)}
                  ModalProps={{
                    keepMounted: false,
                  }}
                  sx={{ zIndex: 1 }}
                >
                  <div className="right-drawer-component-small">
                    <TableRightDrawer
                      toggleDrawer={toggleDrawer}
                      fieldFilter={fieldFilter}
                      selectedFieldFilter={selectedFieldFilter}
                      handleSelectedFieldFilter={handleSelectedFieldFilter}
                      setDraggedFieldOrder={setDraggedFieldOrder}
                    />
                  </div>
                </Drawer>
              </div>
              <CustomToolTip title="Refresh" placement="bottom">
                <button
                  aria-label="Refresh"
                  className="icon-btn-none"
                  onClick={() => window.location.reload()}
                >
                  <Refresh className="icon-size-lg" color="#9F9F9F" />
                </button>
              </CustomToolTip>
            </div>
            <select
              value={selectedFilterOption}
              onChange={handleSelectChange}
              className="filter-input-section right-padding"
            >
              <option value="">All Product</option>
              {Object.entries(dayFilterMapping).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
            </select>
          </div>
        </div>
        <Row className="g-0">
          <Col
            xxl={showFunnel ? 10 : 12}
            xl={showFunnel ? 9 : 12}
            lg={showFunnel ? 8 : 12}
            md={showFunnel ? 8 : 12}
          >
            <Table
              headers={tableHeaders}
              minCellWidth={150}
              tableContent={<TableContent headers={tableHeaders} />}
              tableSortOrder={tableSortOrder}
              handleTableSortOrder={handleTableSortOrder}
            />
            <div className="footer-container left-padding">
              <div className="div-space-between div-align-center footer-content-for-page">
                <div className="div-flex-row cg-20 pagination-container div-align-center">
                  <div className="div-flex-row cg-10 div-align-center">
                    <button
                      aria-label="ArrowLeft"
                      className="icon-btn-none"
                      onClick={handlePreviousPage}
                    >
                      <ArrowLeft
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                      />
                    </button>
                    <input
                      className="page-number-container input-icon-remove text-sm"
                      value={pageNumber < 10 ? `0${pageNumber}` : pageNumber}
                      onChange={handleInputChange}
                      type="number"
                      max={totalPages}
                    />
                    <button
                      aria-label="ArrowRight"
                      className="icon-btn-none"
                      onClick={handleNextPage}
                    >
                      <ArrowRight
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                      />
                    </button>
                  </div>
                  <span className="text-grey text-sm">
                    Page {pageNumber} of {totalPages}
                  </span>
                </div>
              </div>
            </div>
          </Col>
          {showFunnel && (
            <Col
              xxl={showFunnel ? 2 : 0}
              xl={showFunnel ? 3 : 0}
              lg={showFunnel ? 4 : 0}
              md={showFunnel ? 4 : 0}
              className="border-left border-bottom"
            >
              <div className="filter-list-item border-bottom">
                <span className="text-sm text-semi-bold">Filter</span>
              </div>
              <div className="div-flex-column div-space-between rg-20">
                <div className="filter-list-item-container-scroll div-flex-column">
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">Product Type</span>
                    <div className="div-flex-row cg-5">
                      <div className="input-wrapper">
                        <FilterMultiSelect
                          isMulti
                          name="selectedProductType"
                          className="multi-select-input-section"
                          onChange={(selectedOption) =>
                            handleSearchInputChange({
                              target: {
                                name: 'selectedProductType',
                                value: selectedOption,
                              },
                            })
                          }
                          value={formData.selectedProductType || ''}
                          options={productTypeDataOptions}
                          classNamePrefix="react-select"
                          placeholder="Search Type"
                        />
                        <span className="form-input-section-icon">
                          <ArrowRight
                            className="icon-size-sm"
                            color={getColorCode(themeColor)}
                            style={{ transform: 'rotate(90deg)' }}
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column">
                    <div className="div-flex-column rg-5">
                      <span className="text-sm text-semi-bold">
                        Created Date
                      </span>
                      <div className="input-wrapper">
                        <input
                          name="createDate"
                          className="form-input-section input-icon-date"
                          size="small"
                          type="date"
                          value={formData.createDate || ''}
                          onChange={handleSearchInputChange}
                        />
                        <span className="form-input-section-icon">
                          <Calendar
                            className="icon-size-sm"
                            color={getColorCode(themeColor)}
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="div-flex-row div-space-between div-align-center border-top filter-list-item footer-content-for-component">
                  <button
                    className={`common-btn-outlined border-${themeColor}`}
                    onClick={handleResetFilter}
                  >
                    Reset
                  </button>
                  <button
                    className={`common-btn theme-background-${themeColor}`}
                    onClick={handleSearchFilter}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </Col>
          )}
        </Row>
      </div>
    </div>
  );
}
